import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { 
  Table, 
  Card, 
  Form, 
  InputGroup, 
  Spinner, 
  Alert, 
  Container, 
  Pagination,
  Button,
  Modal,
  Row,
  Col
} from 'react-bootstrap';
import Layout from '../components/Layout';
import ScopeIndicator from '../components/ScopeIndicator';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { classroomAPI, scheduleAPI, clearCache } from '../utils/api';
import type { Classroom, CreateClassroomRequest, UpdateClassroomRequest } from '../types/classroom';
import type { Schedule } from '../types/schedule';
import { formatDateTimeInNurseryTimezone, getTodayDateInNurseryTimezone } from '../utils/timezone';
import { useTranslation } from 'react-i18next';

// Lightweight icon replacements to reduce bundle size
const ClassroomIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="me-1">
    <path d="M1.5 14.5A1.5 1.5 0 0 1 0 13V6a1.5 1.5 0 0 1 1.5-1.5h13A1.5 1.5 0 0 1 16 6v7a1.5 1.5 0 0 1-1.5 1.5h-13zm13-1a.5.5 0 0 0 .5-.5V6a.5.5 0 0 0-.5-.5h-13A.5.5 0 0 0 1 6v7a.5.5 0 0 0 .5.5h13z"/>
    <path d="M3 8.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1h-6a.5.5 0 0 1-.5-.5z"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
  </svg>
);

const ClearIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="me-1">
    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
  </svg>
);

const EditIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16" className="text-primary" style={{ cursor: 'pointer' }}>
    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16" className="text-danger" style={{ cursor: 'pointer' }}>
    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
  </svg>
);

// Memoized Classroom Card Component for Mobile
const ClassroomCard = memo(({ classroom, onEdit, canEdit, canDelete, onDelete }: {
  classroom: Classroom;
  onEdit: (classroom: Classroom) => void;
  canEdit: boolean;
  canDelete: boolean;
  onDelete: (classroom: Classroom) => void;
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'classrooms' });
  const { t: tc } = useTranslation('common');

  const { currentNursery } = useAuth();

  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    return formatDateTimeInNurseryTimezone(dateString, currentNursery?.timezone || 'UTC');
  }, [currentNursery?.timezone]);

  return (
    <Card className="mobile-classroom-card m-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-start">
            <ClassroomIcon />
            <div>
              <h6 className="mb-1 fw-bold">{renderCellValue(classroom.name)}</h6>
              <small className="text-muted">{t('ageGroup')}: {renderCellValue(classroom.age_group)}</small>
            </div>
          </div>
          {canEdit && (
            <span
              onClick={() => onEdit(classroom)}
              style={{ cursor: 'pointer' }}
              title={t('editTooltip')}
            >
              <EditIcon />
            </span>
          )}
          {canDelete && (
            <span
              onClick={() => onDelete(classroom)}
              style={{ cursor: 'pointer', marginLeft: '10px' }}
              title={t('deleteTooltip')}
            >
              <DeleteIcon />
            </span>
          )}
        </div>
        
        <div className="mb-2">
          <small className="text-muted d-block">{t('capacity')}</small>
          <span className="fw-medium">{renderCellValue(classroom.capacity)}</span>
        </div>
        
        <div className="mb-2">
          <small className="text-muted d-block">{t('scheduleLabel')}</small>
          <span className="fw-medium">{classroom.schedule_name ? classroom.schedule_name : t('none')}</span>
        </div>
        
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {tc('labels.created')}: {formatDateTime(classroom.created_at)}
          </small>
        </div>
      </Card.Body>
    </Card>
  );
});

ClassroomCard.displayName = 'ClassroomCard';

// Memoized Table Row Component
const ClassroomTableRow = memo(({ 
  classroom, 
  onEdit, 
  onDelete,
  nurseryTimezone,
  canEdit,
  canDelete
}: { 
  classroom: Classroom; 
  onEdit: (classroom: Classroom) => void; 
  onDelete: (classroom: Classroom) => void;
  nurseryTimezone: string;
  canEdit: boolean;
  canDelete: boolean;
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'classrooms' });
  const { t: tc } = useTranslation('common');

  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    if (!dateString) return tc('messages.na');
    return formatDateTimeInNurseryTimezone(dateString, nurseryTimezone);
  }, [nurseryTimezone, tc]);

  return (
    <tr>
      <td>
        <div className="d-flex align-items-center">
          <ClassroomIcon />
          <span className="fw-medium">{renderCellValue(classroom.name)}</span>
        </div>
      </td>
      <td>{renderCellValue(classroom.age_group)}</td>
      <td>{renderCellValue(classroom.capacity)}</td>
      <td><code className="small">{renderCellValue(classroom.nursery_id)}</code></td>
      <td>
        <small className="text-muted">
          {classroom.schedule_name ? classroom.schedule_name : t('none')}
        </small>
      </td>
      <td>
        <small className="text-muted">
          {formatDateTime(classroom.created_at)}
        </small>
      </td>
      <td>
        {canEdit && (
          <span
            onClick={() => onEdit(classroom)}
            style={{ cursor: 'pointer' }}
            title={t('editTooltip')}
          >
            <EditIcon />
          </span>
        )}
        {canDelete && (
          <span
            onClick={() => onDelete(classroom)}
            style={{ cursor: 'pointer', marginLeft: '10px' }}
            title={t('deleteTooltip')}
          >
            <DeleteIcon />
          </span>
        )}
      </td>
    </tr>
  );
});

ClassroomTableRow.displayName = 'ClassroomTableRow';

const Classrooms: React.FC = () => {
  const { currentNursery } = useAuth();
  const { canAdd, canEdit, canDelete } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'classrooms' });
  const { t: tc } = useTranslation('common');
  
  // Get nursery timezone with fallback
  const nurseryTimezone = currentNursery?.timezone || 'UTC';
  
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingClassroom, setDeletingClassroom] = useState<Classroom | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  
  // Edit states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingClassroom, setEditingClassroom] = useState<Classroom | null>(null);
  
  // Additional state for schedules
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState<CreateClassroomRequest & {schedule_id?: number, effective_from?: string}>({
    name: '',
    nursery_id: currentNursery?.nursery_id || '',
    age_group: '',
    capacity: undefined,
    schedule_id: undefined,
    effective_from: ''
  });

  const fetchClassrooms = useCallback(async () => {
    if (!currentNursery) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await classroomAPI.getClassrooms(currentNursery.nursery_id);
      setClassrooms(response.classrooms || []);
    } catch (error: any) {
      console.error('Error fetching classrooms:', error);
      setError(error.response?.data?.message || error.message || t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery, t]);

  const fetchSchedules = useCallback(async () => {
    if (!currentNursery) return;

    try {
      setLoadingSchedules(true);
      const response = await scheduleAPI.getSchedules(currentNursery.nursery_id);
      setSchedules(response.schedules || []);
    } catch (error: any) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  }, [currentNursery]);



  useEffect(() => {
    fetchClassrooms();
  }, [fetchClassrooms]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  useEffect(() => {
    setSearchTerm('');
    setPage(1);
  }, [currentNursery]);

  // Update nursery_id in form when currentNursery changes
  useEffect(() => {
    if (currentNursery) {
      setFormData(prev => ({ ...prev, nursery_id: currentNursery.nursery_id }));
    }
  }, [currentNursery]);

  // Memoized filtered classrooms to prevent unnecessary re-computations
  const filteredClassrooms = useMemo(() => {
    if (!searchTerm.trim()) return classrooms;
    
    const searchLower = searchTerm.toLowerCase();
    return classrooms.filter(classroom => 
      classroom.name?.toLowerCase().includes(searchLower) ||
      classroom.age_group?.toLowerCase().includes(searchLower) ||
      String(classroom.capacity ?? '').includes(searchLower) ||
      classroom.nursery_id?.toLowerCase().includes(searchLower)
    );
  }, [classrooms, searchTerm]);

  const totalPages = Math.ceil(filteredClassrooms.length / rowsPerPage);
  const paginatedClassrooms = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredClassrooms.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredClassrooms, page, rowsPerPage]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to first page when changing items per page
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when searching
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Modal handlers
  const handleShowModal = useCallback(() => {
    setIsEditMode(false);
    setEditingClassroom(null);
    setShowModal(true);
    setModalError(null);
    setModalSuccess(null);
    setFormData({
      name: '',
      nursery_id: currentNursery?.nursery_id || '',
      age_group: '',
      capacity: undefined,
      schedule_id: undefined,
      effective_from: ''
    });
  }, [currentNursery]);

  const handleEditClassroom = useCallback((classroom: Classroom) => {
    setIsEditMode(true);
    setEditingClassroom(classroom);
    setShowModal(true);
    setModalError(null);
    setModalSuccess(null);
    setFormData({
      name: classroom.name,
      nursery_id: classroom.nursery_id,
      age_group: classroom.age_group || '',
      capacity: classroom.capacity,
      schedule_id: classroom.schedule_id != null ? Number(classroom.schedule_id) : undefined,
      effective_from: getTodayDateInNurseryTimezone(nurseryTimezone)
    });
  }, [nurseryTimezone]);


    const handleDeleteClassroom = useCallback((classroom: Classroom) => {
    setDeletingClassroom(classroom);
    setShowDeleteModal(true);
  }, []);


  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setDeletingClassroom(null);
    setModalError(null);
    setModalSuccess(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingClassroom || !currentNursery) return;

    try {
      setModalError(null);
      setLoading(true);
      await classroomAPI.deleteClassroom(deletingClassroom.id, currentNursery.nursery_id);
      setModalSuccess(t('deleteSuccess'));
      await fetchClassrooms();
      setTimeout(() => {
        setShowDeleteModal(false);
        setDeletingClassroom(null);
      }, 1500);
    } catch (error: any) {
      console.error('Error deleting classroom:', error);
      setModalError(error.response?.data?.message || error.message || t('deleteFailed'));
    } finally {
      setLoading(false);
    }
  }, [deletingClassroom, currentNursery, fetchClassrooms, t]);




  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalError(null);
    setModalSuccess(null);
    setIsEditMode(false);
    setEditingClassroom(null);
    setFormData({
      name: '',
      nursery_id: currentNursery?.nursery_id || '',
      age_group: '',
      capacity: undefined,
      schedule_id: undefined,
      effective_from: ''
    });
  }, [currentNursery]);

  const handleFormChange = useCallback((field: keyof (CreateClassroomRequest & {schedule_id?: number, effective_from?: string}), value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentNursery) return;

    try {
      setModalLoading(true);
      setModalError(null);
      setModalSuccess(null);

      if (isEditMode && editingClassroom) {
        // Update existing classroom
        const updateData: UpdateClassroomRequest = {
          name: formData.name,
          age_group: formData.age_group,
          capacity: formData.capacity,
          schedule_id: formData.schedule_id,
          ...(String(formData.schedule_id ?? '') !== String(editingClassroom?.schedule_id ?? '') && formData.effective_from
            ? { effective_from: formData.effective_from }
            : {})
        };
        
        await classroomAPI.updateClassroom(
          editingClassroom.id, 
          updateData, 
          currentNursery.nursery_id
        );
        
        setModalSuccess(t('updateSuccess'));
      } else {
        // Create new classroom
        await classroomAPI.createClassroom(formData);
        setModalSuccess(t('createSuccess'));
      }
      
      // Refresh the list
      await fetchClassrooms();
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseModal();
      }, 2000);
      
    } catch (error: any) {
      console.error('Error saving classroom:', error);
      
      // Handle API errors
      if (error.response?.data?.message) {
        setModalError(error.response.data.message);
      } else {
        setModalError(error.message || t('saveFailed'));
      }
    } finally {
      setModalLoading(false);
    }
  }, [formData, currentNursery, fetchClassrooms, handleCloseModal, isEditMode, editingClassroom, t]);

  // Generate pagination items
  const renderPaginationItems = useCallback(() => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  }, [page, totalPages, handlePageChange]);

  // Force cache clear when nursery changes
  useEffect(() => {
    if (currentNursery) {
      clearCache(`classrooms_${currentNursery.nursery_id}`);
    }
  }, [currentNursery]);

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
          <h2 className="fw-bold text-primary mb-3 mb-md-0">
            {t('title')}
          </h2>
          {currentNursery && canAdd('classrooms') && (
            <Button variant="primary" onClick={handleShowModal}>
              <PlusIcon />
              {t('addClassroom')}
            </Button>
          )}
        </div>

        <ScopeIndicator resource="classrooms" />
        
        {currentNursery && (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: t('title').toLowerCase() })}</strong> {currentNursery.name} (ID: {currentNursery.nursery_id})
          </Alert>
        )}

        {!currentNursery && (
          <Alert variant="warning" className="mb-4">
            {tc('messages.selectNursery', { entity: t('title').toLowerCase() })}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Search Bar */}
        {currentNursery && (
          <Card className="mb-4">
            <Card.Body>
              <InputGroup>
                <InputGroup.Text>
                  <SearchIcon />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <Button variant="outline-secondary" onClick={clearSearch}>
                    <ClearIcon />
                  </Button>
                )}
              </InputGroup>
              {searchTerm && (
                <small className="text-muted mt-2 d-block">
                  {t('found', { count: filteredClassrooms.length, term: searchTerm })}
                </small>
              )}
            </Card.Body>
          </Card>
        )}

        {loading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" className="me-3" />
            <span>{t('loading')}</span>
          </div>
        )}

        {currentNursery && !loading && (
          <Card>
            <Card.Body className="p-0">
              {/* Mobile Card View */}
              <div className="d-md-none">
                {paginatedClassrooms.length > 0 ? (
                  paginatedClassrooms.map((classroom) => (
                    <ClassroomCard
                      key={classroom.id}
                      classroom={classroom}
                      onEdit={handleEditClassroom}
                      onDelete={handleDeleteClassroom}
                      canEdit={canEdit('classrooms')}
                      canDelete={canDelete('classrooms')}
                    />
                  ))
                ) : (
                  <div className="text-center py-5">
                    <ClassroomIcon />
                    <h5>{t('noTitle')}</h5>
                    <p className="text-muted">
                      {searchTerm 
                        ? t('noMatch', { term: searchTerm, nursery: currentNursery.name })
                        : t('noneRegistered', { nursery: currentNursery.name })
                      }
                    </p>
                    {!searchTerm && canAdd('classrooms') && (
                      <Button variant="primary" onClick={handleShowModal}>
                        <PlusIcon />
                        {t('addFirstClassroom')}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t('classroomName')}</th>
                        <th>{t('ageGroup')}</th>
                        <th>{t('capacity')}</th>
                        <th>{t('nurseryId')}</th>
                        <th>{t('scheduleLabel')}</th>
                        <th>{tc('labels.created')}</th>
                        <th>{tc('labels.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedClassrooms.length > 0 ? (
                        paginatedClassrooms.map((classroom) => (
                          <ClassroomTableRow
                            key={classroom.id}
                            classroom={classroom}
                            onEdit={handleEditClassroom}
                            onDelete={handleDeleteClassroom}
                            nurseryTimezone={nurseryTimezone}
                            canEdit={canEdit('classrooms')}
                            canDelete={canDelete('classrooms')}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-5">
                            <ClassroomIcon />
                            <h5>{t('noTitle')}</h5>
                            <p className="text-muted mb-3">
                              {searchTerm 
                                ? t('noMatch', { term: searchTerm, nursery: currentNursery.name })
                                : t('noneRegistered', { nursery: currentNursery.name })
                              }
                            </p>
                            {!searchTerm && canAdd('classrooms') && (
                              <Button variant="primary" onClick={handleShowModal}>
                                <PlusIcon />
                                {t('addFirstClassroom')}
                              </Button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Pagination */}
              {filteredClassrooms.length > rowsPerPage && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <span className="text-muted small">
                    {tc('messages.showing', { from: ((page - 1) * rowsPerPage) + 1, to: Math.min(page * rowsPerPage, filteredClassrooms.length), total: filteredClassrooms.length, entity: t('title').toLowerCase() })}
                  </span>
                  <Pagination className="mb-0">
                    <Pagination.Prev 
                      disabled={page === 1}
                      onClick={() => handlePageChange(page - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next 
                      disabled={page === totalPages}
                      onClick={() => handlePageChange(page + 1)}
                    />
                  </Pagination>
                </div>
              )}

              {/* Items per page selector */}
              <Row className="mt-3 mb-4">
                <Col className="d-flex justify-content-center align-items-center gap-2">
                  <span className="text-muted small">{tc('messages.itemsPerPage')}</span>
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto' }}
                    value={rowsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {!currentNursery && !loading && (
          <Card>
            <Card.Body className="text-center py-5">
              <h5>{tc('messages.selectNurseryTitle')}</h5>
              <p className="text-muted">
                {tc('messages.selectNurseryBody', { entity: t('title').toLowerCase() })}
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Create/Edit Classroom Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEditMode ? t('editTitle') : t('addTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {modalSuccess && (
                <Alert variant="success" className="mb-3">
                  {modalSuccess}
                </Alert>
              )}
              
              {modalError && (
                <Alert variant="danger" className="mb-3">
                  {modalError}
                </Alert>
              )}

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('nameLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder={t('namePlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('ageGroupLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.age_group || ''}
                      onChange={(e) => handleFormChange('age_group', e.target.value)}
                      placeholder={t('ageGroupPlaceholder')}
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('capacityLabel')}</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={formData.capacity ?? ''}
                      onChange={(e) => handleFormChange('capacity', e.target.value ? Number(e.target.value) : undefined)}
                      placeholder={t('capacityPlaceholder')}
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('scheduleLabel')}</Form.Label>
                    <Form.Select
                      value={formData.schedule_id || ''}
                      onChange={(e) => handleFormChange('schedule_id', e.target.value ? parseInt(e.target.value) : undefined)}
                      disabled={loadingSchedules}
                    >
                      <option value="">{t('scheduleOptional')}</option>
                      {schedules.map((schedule) => (
                        <option key={schedule.id} value={schedule.id}>
                          {schedule.name}
                        </option>
                      ))}
                    </Form.Select>
                    {loadingSchedules && (
                      <small className="text-muted">{t('loadingSchedules')}</small>
                    )}
                  </Form.Group>
                </Col>

                {isEditMode && String(formData.schedule_id ?? '') !== String(editingClassroom?.schedule_id ?? '') && (
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{t('effectiveFromLabel')}</Form.Label>
                      <Form.Control
                        type="date"
                        value={formData.effective_from}
                        onChange={(e) => handleFormChange('effective_from', e.target.value)}
                        required
                      />
                      <Form.Text className="text-muted d-block">
                        {t('effectiveFromHint')}
                        <br />
                        <small>{t('effectiveFromDefault')}</small>
                      </Form.Text>
                    </Form.Group>
                  </Col>
                )}

                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('nurseryLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentNursery ? `${currentNursery.name} (${currentNursery.nursery_id})` : ''}
                      readOnly
                      disabled
                      className="bg-light"
                    />
                    <Form.Text className="text-muted">
                      {isEditMode 
                        ? t('nurseryEditHint')
                        : t('nurseryCreateHint')
                      }
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal} disabled={modalLoading}>
                {tc('buttons.cancel')}
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={modalLoading}
              >
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {isEditMode ? tc('loading.updating') : tc('loading.creating')}
                  </>
                ) : (
                  <>
                    {isEditMode ? <EditIcon /> : <PlusIcon />}
                    {isEditMode ? t('updateClassroom') : t('createClassroom')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Delete Classroom Modal */}
                <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                  <Modal.Header closeButton className="py-2 py-md-3">
                    <Modal.Title className="h5 mb-0">{t('deleteTitle')}</Modal.Title>
                  </Modal.Header>
                  <Modal.Body className="py-2 py-md-3">
                    {modalError && (
                      <Alert variant="danger" className="mb-3">
                        {modalError}
                      </Alert>
                    )}
                    {modalSuccess && (
                      <Alert variant="success" className="mb-3">
                        {modalSuccess}
                      </Alert>
                    )}
                    
                    {deletingClassroom && (
                      <div>
                        <p className="mb-3">
                          {t('deleteConfirm', { name: deletingClassroom?.name || t('unknownName') })}
                        </p>
                        <div className="p-3 bg-light rounded">
                          <Row>
                            <Col xs={6}>
                              <small className="text-muted d-block">{t('ageGroup')}</small>
                              <strong>{deletingClassroom.age_group || tc('messages.na')}</strong>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted d-block">{t('capacity')}</small>
                              <strong>{deletingClassroom.capacity ?? tc('messages.na')}</strong>
                            </Col>
                          </Row>
                        </div>
                        <p className="text-danger mt-3 mb-0">
                          <small>{tc('messages.cannotBeUndone')}</small>
                        </p>
                      </div>
                    )}
                  </Modal.Body>
                  <Modal.Footer className="py-2 py-md-3">
                    <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={loading}>
                      {tc('buttons.cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm} disabled={loading}>
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {tc('loading.deleting')}
                        </>
                      ) : (
                        <>
                          <DeleteIcon />
                          {t('deleteClassroom')}
                        </>
                      )}
                    </Button>
                  </Modal.Footer>
                </Modal>
      </Container>
    </Layout>
  );
};

export default Classrooms;
